import * as React from "react";
import { useDispatch, useSelector } from "@teamprevent/admin-redux";
import { isRequestActive, loadAndTrack } from "redux-request-loading";

import { ApplicationState } from "../../applicationState";

import * as selectors from "./selectors";
import { EmployeeRecordDetailModel, EmployeeRecordInRedux } from "./model";
import { loadEmployeeRecordDetailSuccess } from "./actions";
import { employeeRecordApi } from "./employeeRecordApi";
import { LOAD_EMPLOYEE_RECORD_DETAIL } from "./actiontypes";

type useEmployeeRecordDetailsModelReturn =
    { employeeRecord: EmployeeRecordDetailModel; isLoading: false; isError: false; refresh: () => void; isLoaded: true }
    | {
        employeeRecord: EmployeeRecordInRedux | undefined;
        isLoading: boolean;
        isError: boolean;
        refresh: () => void;
        isLoaded: false;
    };

/*
* This hook is used to load the employeeRecord details from the server using either the combination of customerId and employeeRecordId
* Usually these args can be passed in from the route selectors.
* If you don't need the employeeRecord details, you can pass `enable: false` to the hook to disable the loading of the employeeRecord details
* until you need it by calling `refresh()`
*/
export const useEmployeeRecordDetailsModel = (
    args: { employeeRecordId: string; customerId: string },
    { enable }: { enable?: boolean } = {}
): useEmployeeRecordDetailsModelReturn => {
    const dispatch = useDispatch();
    const employeeRecords = useSelector(selectors.employeeRecordsSelector);
    const employeeRecord = employeeRecords.employeeRecords.find(c => c.id === args.employeeRecordId) as EmployeeRecordInRedux | undefined;
    const requestKey = `${LOAD_EMPLOYEE_RECORD_DETAIL}_${args.customerId}_${args.employeeRecordId}`;
    const isLoading = useSelector<ApplicationState, boolean>(state => isRequestActive(state, requestKey));
    const [isError, setIsError] = React.useState(false);

    const refresh = React.useCallback(async () => {
        try {
            // TODO: Replace this with Redux Toolkit
            // @ts-expect-error TS2345
            await loadAndTrack(dispatch, requestKey, (async () => {
                const result = await employeeRecordApi.detail(args.employeeRecordId, args.customerId);
                dispatch(loadEmployeeRecordDetailSuccess(result));
            })());
            setIsError(false);
        } catch (e) {
            setIsError(true);
            throw e;
        }
    }, [args.customerId, dispatch, args.employeeRecordId, requestKey]);

    React.useEffect(() => {
        if (enable === false
            || isLoading
            || isError
            || (employeeRecord && "detailUpdated" in employeeRecord && employeeRecord.detailUpdated.isSameOrAfter(employeeRecord.listUpdated))) {
            return;
        }

        refresh();
    }, [enable, isLoading, employeeRecord, refresh, isError]);

    if (employeeRecord && "detailUpdated" in employeeRecord) {
        return { employeeRecord, isLoading: false, isError: false, refresh, isLoaded: true };
    } else {
        return { employeeRecord, isLoading, isError, refresh, isLoaded: false };
    }
};
